// EditCollectionSelectorPage.jsx
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/EditCollectionSelectorPage.css";
import { getIdentifier } from "../utils/auth";

/* ───── helpers for sharing ───── */
const API = "https://squid-app-6s2hc.ondigitalocean.app";
const APP_BASE = `${window.location.origin}/#`;
const ROUTE = "/view-collection";

const postJSON = (url, body) =>
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

const idPayload = (identifier) =>
  identifier && identifier.includes("@") ? { email: identifier } : { username: identifier };

const ViewCollectionSelectorPage = () => {
  const [collections, setCollections] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const navigate = useNavigate();

  const go = (path) => () => {
    setMobileNavOpen(false);
    navigate(path);
  };

  // mobile drawer state
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const handleDiscover = go("/discover");
  const handleProfile = go("/profile");
  const handleContact = go("/contact");
  const openLearnMore = go("/about");
  const handleHome = go("/home-page");

  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [selectedName, setSelectedName] = useState("");

  // NEW: owner identity (for nicer public links)
  const [ownerUsername, setOwnerUsername] = useState("");
  useEffect(() => {
    const identifier = sessionStorage.getItem("loginIdentifier");
    if (!identifier) return;
    postJSON(`${API}/get-user`, idPayload(identifier))
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) setOwnerUsername((d?.username || "").trim());
      })
      .catch(() => {});
  }, []);

  const handleDeleteClick = (id, name) => {
    setSelectedCollectionId(id);
    setSelectedName(name);
    setShowConfirm(true);
  };

  const fetchCollections = async () => {
    const identifier = getIdentifier();
    if (!identifier) return;

    try {
      const response = await fetch(`${API}/get-collections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(idPayload(identifier)),
      });

      const data = await response.json();
      if (response.ok && data.collections) {
        setCollections(data.collections);
        setTimeout(() => setDataReady(true), 100);
      } else {
        console.error("Error fetching collections:", data.error);
        setDataReady(true);
      }
    } catch (err) {
      console.error("Request failed:", err);
      setDataReady(true);
    }
  };

  useEffect(() => {
    fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (collectionName) => {
    navigate("/view-collection", { state: { collectionName } });
  };

  const handleDelete = async (collectionName) => {
    setShowConfirm(false);

    const identifier = getIdentifier();

    try {
      const response = await fetch(`${API}/delete-collection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...idPayload(identifier), collectionName }),
      });

      const data = await response.json();
      if (data.success) {
        setDataReady(false);
        fetchCollections();
      }
    } catch (error) {}
  };

  

  /* ───── SHARE (no filters) ───── */
  const [shareOpen, setShareOpen] = useState(false);
  const [shareForName, setShareForName] = useState(""); // which collection
  const [shareMode, setShareMode] = useState("public"); // "public" | "owner"
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // ✅ FIX: cache share keys per-collection, so switching collections never “blanks” the input
  const [shareKeys, setShareKeys] = useState({}); // { [collectionName]: key }
  const [keyLoading, setKeyLoading] = useState(false);

  const linkRef = useRef(null);

  const currentShareKey = shareForName ? shareKeys[shareForName] : null;

  const ensureShareKey = useCallback(
    async (collectionName) => {
      const identifier = getIdentifier();
      if (!identifier || !collectionName) return null;

      // already have it
      const cached = shareKeys[collectionName];
      if (cached) return cached;

      const res = await postJSON(`${API}/create-share-key`, {
        ...idPayload(identifier),
        collectionName,
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.key) {
        setShareKeys((prev) => ({ ...prev, [collectionName]: data.key }));
        return data.key;
      }
      return null;
    },
    [shareKeys]
  );

  const isPrivateCollection = useMemo(() => {
    const c = collections.find((x) => x.name === shareForName);
    return !!c?.collectionPrivate;
  }, [collections, shareForName]);

  const makeShareLink = ({ name, mode }) => {
    if (!name) return "";

    if (mode === "public") {
      const params = new URLSearchParams({
        c: name.trim(),
        mode: "public",
        ...(ownerUsername ? { u: ownerUsername } : {}),
      });
      return `${APP_BASE}${ROUTE}?${params.toString()}`;
    }

    // owner view uses share key
    const k = shareKeys[name];
    if (!k) return "";
    const params = new URLSearchParams({ k, mode: "owner" });
    return `${APP_BASE}${ROUTE}?${params.toString()}`;
  };

  const openShare = async (name, isPrivate) => {
    setShareForName(name);
    setCopied(false);
    setShareMenuOpen(false);

    // default: private => owner link, public => public link
    setShareMode(isPrivate ? "owner" : "public");

    setShareOpen(true);

    // prefetch owner key for private (so input doesn’t go blank)
    if (isPrivate) {
      setKeyLoading(true);
      try {
        await ensureShareKey(name);
      } finally {
        setKeyLoading(false);
      }
    }
  };

  const linkValue = useMemo(() => {
    return makeShareLink({ name: shareForName, mode: shareMode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareForName, shareMode, shareKeys, ownerUsername]);

  const copyCurrentLink = async () => {
    // if owner link requested but not ready, prepare it first
    if (shareMode === "owner" && !currentShareKey) {
      setKeyLoading(true);
      try {
        await ensureShareKey(shareForName);
      } finally {
        setKeyLoading(false);
      }
    }

    const out = makeShareLink({ name: shareForName, mode: shareMode });
    if (!out) return;

    try {
      await navigator.clipboard.writeText(out);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      linkRef.current?.select?.();
    } catch {}
  };

  if (!dataReady) return null;

  return (
    <div className={`nav-shell ${mobileNavOpen ? "nav-shell--open" : ""}`}>
<aside className={`slide-menu ${mobileNavOpen ? "open" : ""}`}>
  <button
    className="menu-close"
    onClick={() => setMobileNavOpen(false)}
  >
    ×
  </button>

  <nav className="menu-nav">
    <button onClick={handleHome}>
      home
    </button>
  

    <button onClick={handleProfile}>
      profile
    </button>

    <div className="menu-divider" />

     <button onClick={handleDiscover}>
      explore items
    </button>

     <button onClick={openLearnMore}>
      learn more
    </button>

    <button onClick={handleContact}>
      contact t.c.p.
    </button>

   
  </nav>
</aside>

      {!mobileNavOpen && (
        <header className="nav-burger-wrap" aria-label="page controls">
          <button
            type="button"
            className="nav-burger"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open menu"
          >
            ☰
          </button>
        </header>
      )}

      <div className="edit-collection-page">
        <main className={`edit-selector-page ${dataReady ? "ready" : ""}`}>
          {collections.length === 0 ? (
            <>
              <h1>
                ┌─ no <span className="highlight">collections</span> found
              </h1>
              <button className="create-new-button" onClick={() => navigate("/upload-collection")}>
                + create a new collection
              </button>
            </>
          ) : (
            <h1>
              ┌─ select a <span className="highlight">collection</span> to{" "}
              <span className="highlight">view</span>
            </h1>
          )}

          <div className="collection-list">
            {collections.map((col) => (
              <div key={col.name} className="collection-card">
                <div className="collection-info" onClick={() => handleSelect(col.name)}>
                  <h2>{col.name}</h2>
                  <p>total {col.itemCount}</p>
                  <p>privacy: {col.collectionPrivate ? "🔐" : "🔓"}</p>
                </div>

                <div className="collection-actions">
                  <button
                    className="secondary-button"
                    onClick={() => navigate("/edit-collection", { state: { collectionName: col.name } })}
                  >
                    [ edit collection ]
                  </button>


                  <button className="delete-button" onClick={() => handleDeleteClick(col.name, col.name)}>
                    [ delete collection ]
                  </button>
                </div>
              </div>
            ))}
          </div>

          {showConfirm && (
            <div className="terminal-modal-overlay">
              <div className="ecsp-terminal-modal">
                <p>
                  are you sure you want to delete "<strong>{selectedName}</strong>"?
                </p>
                <div className="modal-actions">
                  <button className="modal-button highlight" onClick={() => handleDelete(selectedCollectionId)}>
                    yes
                  </button>
                  <button className="modal-button" onClick={() => setShowConfirm(false)}>
                    no
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Share modal */}
          {shareOpen && (
  <div
    className="vc-modal-overlay"
    role="dialog"
    aria-modal="true"
    onClick={() => setShareOpen(false)}
  >
    <div className="vc-modal" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setShareOpen(false)}
        aria-label="Close"
        className="vc-modal-close"
      >
        ×
      </button>

      <p className="vc-modal-title">
        share <strong>"{shareForName}"</strong>
      </p>

      <div className="vc-share-row">
        <input
          ref={linkRef}
          className="vc-share-input"
          type="text"
          readOnly
          value={linkValue}
          onFocus={(e) => e.target.select()}
          placeholder={
            shareMode === "owner" && !currentShareKey
              ? "preparing owner link…"
              : ""
          }
        />

     {!isPrivateCollection && (
  <div className="vc-share-select">
    <button
      type="button"
      onClick={() => setShareMenuOpen((o) => !o)}
      aria-haspopup="listbox"
      aria-expanded={shareMenuOpen}
      className="vc-share-select-btn"
    >
      {shareMode === "public" ? "public view" : "my view"}{" "}
      <span className="vc-caret">▾</span>
    </button>

    {shareMenuOpen && (
      <div role="listbox" className="vc-share-menu">
        <button
          role="option"
          aria-selected={shareMode === "public"}
          className="vc-share-menu-item"
          onClick={() => {
            setShareMode("public");
            setShareMenuOpen(false);
          }}
        >
          {shareMode === "public" ? "✓ " : ""}
          public view
        </button>

        <button
          role="option"
          aria-selected={shareMode === "owner"}
          className="vc-share-menu-item"
          onClick={async () => {
            setShareMode("owner");
            setShareMenuOpen(false);

            if (!shareKeys[shareForName]) {
              setKeyLoading(true);
              try {
                await ensureShareKey(shareForName);
              } finally {
                setKeyLoading(false);
              }
            }
          }}
        >
          {shareMode === "owner" ? "✓ " : ""}
          my view
        </button>
      </div>
    )}
  </div>
)}

        <button
          type="button"
          className="vc-share-copy"
          onClick={copyCurrentLink}
          disabled={keyLoading || (shareMode === "owner" && !currentShareKey)}
        >
          {copied ? "copied ✓" : keyLoading ? "preparing…" : "copy link"}
        </button>
      </div>
    </div>
  </div>
)}

        </main>
      </div>
    </div>
  );
};

export default ViewCollectionSelectorPage;
